import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import type {
  LiveRecordingSnapshot,
  MessageReply,
  PinnedMessage,
  WaitingParticipant,
} from "@/api/types"
import type { RawTranscriptionData } from "@/lib/dataChannel"
import { logoutAction } from "@/store/session/logoutAction"
import type { MessageReactions, ReplyContext } from "@/types/roomFeatures"

const MAX_CHAT_MESSAGES = 500
const MAX_TRANSCRIPTION_MESSAGES = 500
const MAX_LIVE_RECORDING_EVENT_IDS = 100

interface SimpleSender {
  identity?: string
  name?: string
}

export interface ReduxPinnedMessage {
  id: string
  text: string
  sender: SimpleSender
}

interface ReduxChatMessage {
  id: string
  message: string
  timestamp: number
  sender?: {
    identity: string
    name: string
    avatar: string
  }
  type: "text" | "system"
  recordingKind?:
    | "recording_started"
    | "recording_paused"
    | "recording_resumed"
    | "recording_stopped"
  replyTo?: MessageReply
}

interface ReduxEmojiEvent {
  id: string
  emoji: string
  senderIdentity: string
  senderName: string
  timestamp: number
}

interface ReduxTranslationEntry {
  messageId: string
  language: string
  text: string
}

interface RoomFeaturesState {
  hostIdentity: string
  cohosts: string[]
  raisedHands: Record<string, number>
  pinnedMessage: ReduxPinnedMessage | null
  messageReactions: MessageReactions
  replyContexts: ReplyContext
  chatMessages: ReduxChatMessage[]
  lastReadTimestamp: number
  lastReadTranscriptionTimestamp: number
  token: string
  roomId: string
  emojiEvents: ReduxEmojiEvent[]
  transcriptionMessages: RawTranscriptionData[]
  translationEntries: Record<string, Record<string, string>>
  aiTranslatorLanguage: string
  ui: {
    isChatOpen: boolean
    isParticipantsOpen: boolean
    isAskAiOpen: boolean
    showReactions: boolean
    showMoreMenu: boolean
    showLanguageSelector: boolean
    showVideoModal: boolean
    expandedParticipantId: string | null
  }
  selectedReplyTarget: {
    messageId: string
    content: string
    senderName?: string
    timestamp: number
  } | null
  waitingParticipants: WaitingParticipant[]
  admitEveryoneAutomatically: boolean
  liveRecording: LiveRecordingSnapshot | null
  liveRecordingEventIds: string[]
}

const initialState: RoomFeaturesState = {
  hostIdentity: "",
  cohosts: [],
  raisedHands: {},
  pinnedMessage: null,
  messageReactions: {},
  replyContexts: {},
  chatMessages: [],
  lastReadTimestamp: 0,
  lastReadTranscriptionTimestamp: 0,
  token: "",
  roomId: "",
  emojiEvents: [],
  transcriptionMessages: [],
  translationEntries: {},
  aiTranslatorLanguage: "",
  ui: {
    isChatOpen: false,
    isParticipantsOpen: false,
    isAskAiOpen: false,
    showReactions: false,
    showMoreMenu: false,
    showLanguageSelector: false,
    showVideoModal: false,
    expandedParticipantId: null,
  },
  selectedReplyTarget: null,
  waitingParticipants: [],
  admitEveryoneAutomatically: false,
  liveRecording: null,
  liveRecordingEventIds: [],
}

const roomFeaturesSlice = createSlice({
  name: "roomFeatures",
  initialState,
  reducers: {
    initializeRoom: (
      state,
      action: PayloadAction<{
        hostIdentity?: string
        token?: string
        roomId?: string
        raisedHands?: string[]
        cohosts?: string[]
        pinnedMessage?: PinnedMessage
        liveRecording?: LiveRecordingSnapshot
      }>,
    ) => {
      state.hostIdentity = action.payload.hostIdentity || ""
      state.token = action.payload.token || ""
      state.roomId = action.payload.roomId || ""
      state.pinnedMessage = action.payload.pinnedMessage || null
      state.cohosts = action.payload.cohosts ?? []
      state.liveRecording =
        action.payload.liveRecording ?? state.liveRecording

      if (action.payload.raisedHands) {
        const baseTimestamp = Date.now()
        state.raisedHands = action.payload.raisedHands.reduce<
          Record<string, number>
        >((acc, identity, index) => {
          acc[identity] = baseTimestamp + index
          return acc
        }, {})
      }
    },

    resetRoomFeatures: () => initialState,

    addChatMessage: (state, action: PayloadAction<ReduxChatMessage>) => {
      if (state.chatMessages.some((m) => m.id === action.payload.id)) return
      state.chatMessages.push(action.payload)
      if (state.chatMessages.length > MAX_CHAT_MESSAGES) {
        state.chatMessages.shift()
      }
    },

    addCohost: (state, action: PayloadAction<string>) => {
      if (!state.cohosts.includes(action.payload)) {
        state.cohosts.push(action.payload)
      }
    },

    removeCohost: (state, action: PayloadAction<string>) => {
      state.cohosts = state.cohosts.filter((id) => id !== action.payload)
    },

    raiseHand: (
      state,
      action: PayloadAction<{ identity: string; raisedAt: number }>,
    ) => {
      state.raisedHands[action.payload.identity] = action.payload.raisedAt
    },

    lowerHand: (state, action: PayloadAction<string>) => {
      delete state.raisedHands[action.payload]
    },

    lowerAllHands: (state) => {
      state.raisedHands = {}
    },

    setRaisedHands: (
      state,
      action: PayloadAction<{ identities: string[]; timestamp: number }>,
    ) => {
      const next: Record<string, number> = {}
      action.payload.identities.forEach((identity, index) => {
        next[identity] =
          state.raisedHands[identity] ?? action.payload.timestamp + index
      })
      state.raisedHands = next
    },

    removeParticipantHand: (state, action: PayloadAction<string>) => {
      delete state.raisedHands[action.payload]
    },

    setPinnedMessage: (
      state,
      action: PayloadAction<ReduxPinnedMessage | null>,
    ) => {
      state.pinnedMessage = action.payload
    },

    addReaction: (
      state,
      action: PayloadAction<{
        messageId: string
        emoji: string
        userIdentity: string
      }>,
    ) => {
      const { messageId, emoji, userIdentity } = action.payload
      if (!state.messageReactions[messageId]) {
        state.messageReactions[messageId] = {}
      }
      if (!state.messageReactions[messageId][emoji]) {
        state.messageReactions[messageId][emoji] = []
      }
      if (!state.messageReactions[messageId][emoji].includes(userIdentity)) {
        state.messageReactions[messageId][emoji].push(userIdentity)
      }
    },

    removeReaction: (
      state,
      action: PayloadAction<{
        messageId: string
        emoji: string
        userIdentity: string
      }>,
    ) => {
      const { messageId, emoji, userIdentity } = action.payload
      if (state.messageReactions[messageId]?.[emoji]) {
        const idx =
          state.messageReactions[messageId][emoji].indexOf(userIdentity)
        if (idx > -1) {
          state.messageReactions[messageId][emoji].splice(idx, 1)
          if (state.messageReactions[messageId][emoji].length === 0) {
            delete state.messageReactions[messageId][emoji]
          }
          if (Object.keys(state.messageReactions[messageId]).length === 0) {
            delete state.messageReactions[messageId]
          }
        }
      }
    },

    addReplyContext: (
      state,
      action: PayloadAction<{
        replyId: string
        originalMessage: {
          id: string
          text: string
          sender: string
          timestamp: number
        }
        replyMessage: {
          text: string
          sender: string
          timestamp: number
        }
      }>,
    ) => {
      const { replyId, originalMessage, replyMessage } = action.payload
      state.replyContexts[replyId] = {
        originalMessage,
        replyMessage,
      }
    },

    setSelectedReplyTarget: (
      state,
      action: PayloadAction<RoomFeaturesState["selectedReplyTarget"]>,
    ) => {
      state.selectedReplyTarget = action.payload
    },

    clearReplyTarget: (state) => {
      state.selectedReplyTarget = null
    },

    markChatRead: (state) => {
      const lastMsg = state.chatMessages[state.chatMessages.length - 1]
      if (lastMsg) {
        state.lastReadTimestamp = lastMsg.timestamp
      }
    },

    markTranscriptionRead: (state) => {
      const lastMsg =
        state.transcriptionMessages[state.transcriptionMessages.length - 1]
      if (lastMsg) {
        state.lastReadTranscriptionTimestamp = lastMsg.timestamp
      }
    },

    addEmojiEvent: (state, action: PayloadAction<ReduxEmojiEvent>) => {
      state.emojiEvents.push(action.payload)
      if (state.emojiEvents.length > 50) {
        state.emojiEvents.shift()
      }
    },

    addTranscriptionMessage: (
      state,
      action: PayloadAction<RawTranscriptionData>,
    ) => {
      if (
        state.transcriptionMessages.some(
          (message) => message.messageId === action.payload.messageId,
        )
      ) {
        return
      }

      state.transcriptionMessages.push(action.payload)
      if (state.transcriptionMessages.length > MAX_TRANSCRIPTION_MESSAGES) {
        const removed = state.transcriptionMessages.shift()
        if (removed) {
          delete state.translationEntries[removed.messageId]
        }
      }
    },

    addTranslationEntry: (
      state,
      action: PayloadAction<ReduxTranslationEntry>,
    ) => {
      const { messageId, language, text } = action.payload
      if (!state.translationEntries[messageId]) {
        state.translationEntries[messageId] = {}
      }
      state.translationEntries[messageId][language] = text
    },

    setChatOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.isChatOpen = action.payload
      if (action.payload) {
        state.ui.isParticipantsOpen = false
        state.ui.isAskAiOpen = false
      }
    },

    setParticipantsOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.isParticipantsOpen = action.payload
      if (action.payload) {
        state.ui.isChatOpen = false
        state.ui.isAskAiOpen = false
      }
    },

    setAskAiOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.isAskAiOpen = action.payload
      if (action.payload) {
        state.ui.isChatOpen = false
        state.ui.isParticipantsOpen = false
      }
    },

    setReactionsOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.showReactions = action.payload
    },

    setMoreMenuOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.showMoreMenu = action.payload
    },

    setLanguageSelectorOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.showLanguageSelector = action.payload
    },

    setVideoModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.showVideoModal = action.payload
    },

    setExpandedParticipantId: (state, action: PayloadAction<string | null>) => {
      state.ui.expandedParticipantId = action.payload
    },

    setAiTranslatorLanguage: (state, action: PayloadAction<string>) => {
      state.aiTranslatorLanguage = action.payload
    },

    setWaitingQueue: (
      state,
      action: PayloadAction<{
        waiting: WaitingParticipant[]
        admitEveryoneAutomatically: boolean
      }>,
    ) => {
      state.waitingParticipants = action.payload.waiting
      state.admitEveryoneAutomatically = action.payload.admitEveryoneAutomatically
    },

    setAdmitEveryoneAutomatically: (state, action: PayloadAction<boolean>) => {
      state.admitEveryoneAutomatically = action.payload
    },

    setLiveRecordingSnapshot: (
      state,
      action: PayloadAction<LiveRecordingSnapshot>,
    ) => {
      if (
        state.liveRecording !== null &&
        action.payload.version < state.liveRecording.version
      ) {
        return
      }
      state.liveRecording = action.payload
    },

    setLiveRecordingPhaseStarting: (state) => {
      if (
        !state.liveRecording ||
        state.liveRecording.phase === "recording" ||
        state.liveRecording.phase === "paused"
      ) {
        return
      }
      if (state.liveRecording.phase === "idle") {
        state.liveRecording.phase = "starting"
      }
    },

    markLiveRecordingEventProcessed: (state, action: PayloadAction<string>) => {
      if (state.liveRecordingEventIds.includes(action.payload)) {
        return
      }
      state.liveRecordingEventIds.push(action.payload)
      if (state.liveRecordingEventIds.length > MAX_LIVE_RECORDING_EVENT_IDS) {
        state.liveRecordingEventIds.shift()
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logoutAction, () => initialState)
  },
})

export const {
  initializeRoom,
  resetRoomFeatures,
  addChatMessage,
  markChatRead,
  markTranscriptionRead,
  addEmojiEvent,
  addTranscriptionMessage,
  addTranslationEntry,
  addCohost,
  removeCohost,
  raiseHand,
  lowerHand,
  lowerAllHands,
  setRaisedHands,
  removeParticipantHand,
  setPinnedMessage,
  addReaction,
  removeReaction,
  addReplyContext,
  setSelectedReplyTarget,
  clearReplyTarget,
  setChatOpen,
  setParticipantsOpen,
  setReactionsOpen,
  setMoreMenuOpen,
  setLanguageSelectorOpen,
  setVideoModalOpen,
  setExpandedParticipantId,
  setAiTranslatorLanguage,
  setAskAiOpen,
  setWaitingQueue,
  setAdmitEveryoneAutomatically,
  setLiveRecordingSnapshot,
  setLiveRecordingPhaseStarting,
  markLiveRecordingEventProcessed,
} = roomFeaturesSlice.actions

export default roomFeaturesSlice.reducer
